import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);
  const catColor = cat?.color === 'accent'
    ? { bg: 'bg-accent-100', text: 'text-accent-800' }
    : { bg: 'bg-primary-100', text: 'text-primary-800' };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <article className="pt-24 pb-16 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: cat?.name || '', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${catColor.bg} ${catColor.text}`}>
                  {cat?.name}
                </span>
                <span className="text-sm text-foreground-400">{article.date}</span>
                <span className="text-sm text-foreground-400">{article.readTime}</span>
              </div>

              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="aspect-[21/9] rounded-xl overflow-hidden bg-background-100 mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="article-content text-base"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <h3 className="font-heading text-lg font-semibold text-foreground-900">分享文章</h3>
                <div className="flex items-center gap-2">
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-16 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                      {ra.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{ra.date}</span>
                      <span>{ra.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: { '@type': 'Organization', name: '磁力金牛' },
          publisher: { '@type': 'Organization', name: '磁力金牛' },
        }) }} />
      </main>
      <Footer />
    </>
  );
}